import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { diagnosticsClient } from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useWorkspaceStore } from "@/lib/workspace";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { useServerGrid } from "@/lib/use-server-grid";

export const Route = createFileRoute("/diagnostics/storage")({
	component: DiagnosticsStoragePage,
	validateSearch: (search: Record<string, unknown>) => ({
		create: search.create === "1" ? "1" : undefined,
	}),
});

interface StorageRow {
	id: string;
	targetName: string;
	targetType: string;
	s3Endpoint: string;
	s3Bucket: string;
	s3Region: string;
	s3PathPrefix: string;
	credentialStrategy: string;
	retentionHotDays: number;
	retentionWarmDays: number;
	retentionColdDays: number;
	status: string;
	updatedAt: unknown;
}

function DiagnosticsStoragePage() {
	const { t } = useTranslation("diagnostics");
	useBreadcrumbs([{ label: t("title"), href: "/diagnostics" }, { label: t("sidebar_storage") }]);
	const search = Route.useSearch();
	const { onGridReady, onStateChanged } = useGridLayout("diagnostics-storage");
	const [createOpen, setCreateOpen] = useState(search.create === "1");
	const { selectedRows, onSelectionChanged, ActionBar } =
		useGridSelection<StorageRow>();

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<StorageRow>({
		fetchRows: async () => {
			const resp = await diagnosticsClient.listStorageTargets({});
			const rows = (resp.targets ?? []) as StorageRow[];
			return {
				rows,
				totalCount: rows.length,
			};
		},
		colToField: {
			targetName: "target_name",
			targetType: "target_type",
			s3Endpoint: "s3_endpoint",
			s3Bucket: "s3_bucket",
			s3Region: "s3_region",
			credentialStrategy: "credential_strategy",
		},
	});

	// ── Column definitions ──
	const columnDefs = useMemo<ColDef<StorageRow>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "targetName",
				headerName: t("storage_col_name"),
				flex: 1,
				minWidth: 150,
			},
			{
				field: "targetType",
				headerName: t("storage_col_scope"),
				width: 110,
				cellRenderer: (p: { value: string }) => (
					<span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 9999, fontSize: 11, lineHeight: "18px", border: "1px solid color-mix(in srgb, hsl(var(--muted-foreground)) 20%, transparent)", color: "hsl(var(--muted-foreground))" }}>
						{t(`enum_target_${p.value}`, { defaultValue: p.value ?? "" })}
					</span>
				),
			},
			{
				field: "s3Endpoint",
				headerName: t("storage_col_endpoint", { defaultValue: "Endpoint" }),
				width: 180,
				valueFormatter: (p) => p.value || "\u2014",
			},
			{
				field: "s3Bucket",
				headerName: t("storage_col_bucket"),
				width: 140,
			},
			{
				field: "s3Region",
				headerName: t("storage_col_region", { defaultValue: "Регион" }),
				width: 100,
			},
			{
				colId: "retention",
				headerName: t("storage_col_retention"),
				width: 160,
				filter: false,
				valueGetter: (p) => {
					const d = p.data;
					if (!d) return "";
					return t("storage_retention_format", {
						hot: d.retentionHotDays,
						warm: d.retentionWarmDays,
						cold: d.retentionColdDays,
					});
				},
			},
			{
				field: "status",
				headerName: t("storage_col_status"),
				width: 120,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					const color =
						p.value === "active"
							? "hsl(var(--chart-2))"
							: p.value === "decommissioning"
								? "hsl(var(--destructive))"
								: "hsl(var(--muted-foreground))";
					return (
						<span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 9999, fontSize: 11, lineHeight: "18px", border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`, color }}>
							{t(`enum_status_${p.value}`, { defaultValue: p.value })}
						</span>
					);
				},
			},
			{
				field: "credentialStrategy",
				headerName: t("storage_col_credential"),
				width: 180,
				valueFormatter: (p) =>
					t(`enum_credential_${p.value}`, { defaultValue: p.value ?? "" }),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	// ── Status bar ──
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: `${totalRows} ${t("storage_status_count", { defaultValue: "хранилищ" })}`,
				align: "left" as const,
			},
			...(selectedRows.length > 0
				? [
						{
							id: "selection",
							content: t("rows_selected", {
								count: selectedRows.length,
								defaultValue: `${selectedRows.length} выбрано`,
							}),
							align: "right" as const,
						},
					]
				: []),
		],
		[totalRows, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption("quickFilterText", e.target.value)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("storage_create")}
				</Button>
			</div>
			<div
				className="flex-1 ag-theme-quartz h-full"
				style={{ width: "100%" }}
			>
				<AgGridReact<StorageRow>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("loading", { defaultValue: "Загрузка" })}...</span>`}
				/>
			</div>

			<ActionBar />
			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<DialogContent className="sm:max-w-2xl">
					<CreateStorageTargetForm
						onSuccess={() => {
							setCreateOpen(false);
							refreshGrid();
							toast.success(t("toast_storage_created"));
						}}
						onCancel={() => setCreateOpen(false)}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}

function CreateStorageTargetForm({
	onSuccess,
	onCancel,
}: { onSuccess: () => void; onCancel: () => void }) {
	const { t } = useTranslation("diagnostics");
	const workspaceId = useWorkspaceStore((state) => state.selectedId);
	const [targetType, setTargetType] = useState("workspace");
	const [targetId, setTargetId] = useState("");
	const [targetName, setTargetName] = useState("");
	const [s3Endpoint, setS3Endpoint] = useState("");
	const [s3Bucket, setS3Bucket] = useState("");
	const [s3Region, setS3Region] = useState("us-east-1");
	const [s3PathPrefix, setS3PathPrefix] = useState("diag/");
	const [credentialStrategy, setCredentialStrategy] = useState(
		"platform_managed",
	);
	const [secretRef, setSecretRef] = useState("");
	const [assumeRoleArn, setAssumeRoleArn] = useState("");
	const [brokerBaseUrl, setBrokerBaseUrl] = useState("");
	const [retentionHotDays, setRetentionHotDays] = useState("30");
	const [retentionWarmDays, setRetentionWarmDays] = useState("90");
	const [retentionColdDays, setRetentionColdDays] = useState("365");
	const [status, setStatus] = useState("active");

	const effectiveTargetId = useMemo(
		() => (targetType === "workspace" ? (workspaceId ?? "") : targetId),
		[targetId, targetType, workspaceId],
	);

	const mutation = useMutation({
		mutationFn: () =>
			diagnosticsClient.createStorageTarget({
				targetType,
				targetId: effectiveTargetId,
				targetName,
				s3Endpoint,
				s3Bucket,
				s3Region,
				s3PathPrefix,
				credentialStrategy,
				secretRef,
				assumeRoleArn,
				brokerBaseUrl,
				retentionHotDays: Number.parseInt(retentionHotDays, 10) || 30,
				retentionWarmDays: Number.parseInt(retentionWarmDays, 10) || 90,
				retentionColdDays: Number.parseInt(retentionColdDays, 10) || 365,
				status,
			}),
		onSuccess,
		onError: (err: Error) =>
			toast.error(t("toast_error_generic"), { description: err.message }),
	});

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t("storage_create_title")}</DialogTitle>
				<DialogDescription>{t("storage_create_description")}</DialogDescription>
			</DialogHeader>
			<div className="space-y-5 py-4 max-h-[60vh] overflow-y-auto">
				{/* Section 1: Basic */}
				<div>
					<p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground pb-2">
						{t("storage_section_basic")}
					</p>
					<div className="grid gap-3 md:grid-cols-2">
						<div className="space-y-2">
							<Label>{t("storage_target_type")}</Label>
							<Select value={targetType} onValueChange={setTargetType}>
								<SelectTrigger className="w-full">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="workspace">
										{t("enum_target_workspace")}
									</SelectItem>
									<SelectItem value="party">
										{t("enum_target_party")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>{t("storage_target_name")}</Label>
							<Input
								value={targetName}
								onChange={(event) => setTargetName(event.target.value)}
							/>
						</div>
						{targetType === "party" && (
							<div className="space-y-2">
								<Label>{t("storage_target_id")}</Label>
								<Input
									value={targetId}
									onChange={(event) => setTargetId(event.target.value)}
									placeholder="UUID"
								/>
								<p className="text-xs text-muted-foreground">
									{t("storage_party_scope_hint")}
								</p>
							</div>
						)}
						<div className="space-y-2">
							<Label>{t("storage_status")}</Label>
							<Select value={status} onValueChange={setStatus}>
								<SelectTrigger className="w-full">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="active">
										{t("enum_status_active")}
									</SelectItem>
									<SelectItem value="suspended">
										{t("enum_status_suspended")}
									</SelectItem>
									<SelectItem value="decommissioning">
										{t("enum_status_decommissioning")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
				</div>

				{/* Section 2: S3 (hidden for platform_managed) */}
				{credentialStrategy !== "platform_managed" && (
					<div className="border-t pt-4">
						<p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground pb-2">
							{t("storage_section_s3")}
						</p>
						<div className="grid gap-3 md:grid-cols-2">
							<div className="space-y-2">
								<Label>{t("storage_endpoint")}</Label>
								<Input
									placeholder="https://s3.amazonaws.com"
									value={s3Endpoint}
									onChange={(event) => setS3Endpoint(event.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("storage_bucket")}</Label>
								<Input
									value={s3Bucket}
									onChange={(event) => setS3Bucket(event.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("storage_region")}</Label>
								<Input
									value={s3Region}
									onChange={(event) => setS3Region(event.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("storage_path_prefix")}</Label>
								<Input
									value={s3PathPrefix}
									onChange={(event) => setS3PathPrefix(event.target.value)}
								/>
							</div>
						</div>
					</div>
				)}

				{/* Section 3: Credentials */}
				<div className="border-t pt-4">
					<p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground pb-2">
						{t("storage_section_credentials")}
					</p>
					<div className="grid gap-3 md:grid-cols-2">
						<div className="space-y-2">
							<Label>{t("storage_credential_strategy")}</Label>
							<Select
								value={credentialStrategy}
								onValueChange={setCredentialStrategy}
							>
								<SelectTrigger className="w-full">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="platform_managed">
										{t("enum_credential_platform_managed")}
									</SelectItem>
									<SelectItem value="workspace_managed_secret_ref">
										{t("enum_credential_workspace_managed_secret_ref")}
									</SelectItem>
									<SelectItem value="assume_role">
										{t("enum_credential_assume_role")}
									</SelectItem>
									<SelectItem value="brokered_replay">
										{t("enum_credential_brokered_replay")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{credentialStrategy === "platform_managed" && (
							<div className="md:col-span-2">
								<p className="text-xs text-muted-foreground">
									{t("storage_platform_managed_hint")}
								</p>
							</div>
						)}
						{credentialStrategy === "workspace_managed_secret_ref" && (
							<div className="space-y-2">
								<Label>{t("storage_secret_ref")}</Label>
								<Input
									value={secretRef}
									onChange={(event) => setSecretRef(event.target.value)}
								/>
							</div>
						)}
						{credentialStrategy === "assume_role" && (
							<div className="space-y-2">
								<Label>{t("storage_assume_role")}</Label>
								<Input
									value={assumeRoleArn}
									onChange={(event) => setAssumeRoleArn(event.target.value)}
								/>
							</div>
						)}
						{credentialStrategy === "brokered_replay" && (
							<div className="space-y-2">
								<Label>{t("storage_broker_url")}</Label>
								<Input
									value={brokerBaseUrl}
									onChange={(event) => setBrokerBaseUrl(event.target.value)}
								/>
							</div>
						)}
					</div>
				</div>

				{/* Section 4: Retention */}
				<div className="border-t pt-4">
					<p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground pb-2">
						{t("storage_section_retention")}
					</p>
					<div className="grid gap-3 md:grid-cols-3">
						<div className="space-y-2">
							<Label>{t("storage_retention_hot")}</Label>
							<Input
								type="number"
								value={retentionHotDays}
								onChange={(event) => setRetentionHotDays(event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("storage_retention_warm")}</Label>
							<Input
								type="number"
								value={retentionWarmDays}
								onChange={(event) => setRetentionWarmDays(event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("storage_retention_cold")}</Label>
							<Input
								type="number"
								value={retentionColdDays}
								onChange={(event) => setRetentionColdDays(event.target.value)}
							/>
						</div>
					</div>
				</div>
			</div>
			<DialogFooter>
				<Button variant="outline" onClick={onCancel}>
					{t("cancel")}
				</Button>
				<Button
					onClick={() => mutation.mutate()}
					disabled={
						mutation.isPending ||
						!effectiveTargetId ||
						!targetName.trim() ||
						(credentialStrategy !== "platform_managed" &&
							(!s3Endpoint.trim() || !s3Bucket.trim()))
					}
				>
					{mutation.isPending ? (
						<>
							<Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
							{t("storage_creating")}
						</>
					) : (
						t("storage_create")
					)}
				</Button>
			</DialogFooter>
		</>
	);
}
